import {xml} from '@xmpp/client';
import {GroupEvent, XMPPMentionMetadata} from '../types';
import {v4 as uuidv4} from 'uuid';

export class Logger {
    private static isDebug = process.env.NODE_ENV === 'development';

    static log(prefix: string, ...args: any[]) {
        if (this.isDebug) {
            console.log(`[${prefix}]`, ...args);
        }
    }

    static info(prefix: string, ...args: any[]) {
        if (this.isDebug) {
            console.info(`[${prefix}]`, ...args);
        }
    }

    static warn(prefix: string, ...args: any[]) {
        if (this.isDebug) {
            console.warn(`[${prefix}]`, ...args);
        }
    }

    static error(prefix: string, ...args: any[]) {
        console.error(`[${prefix}]`, ...args);
    }

    static debug(prefix: string, ...args: any[]) {
        if (this.isDebug) {
            console.debug(`[${prefix}]`, ...args);
        }
    }
}

export class FileDetectorService {
    private static readonly IMAGE_EXTENSIONS = new Set([
        'jpg',
        'jpeg',
        'png',
        'gif',
        'webp',
        'svg',
        'bmp',
        'ico',
        'tiff',
        'tif',
    ]);

    private static readonly AUDIO_EXTENSIONS = new Set([
        'mp3',
        'wav',
        'ogg',
        'aac',
        'flac',
        'm4a',
        'wma',
        'webm',
    ]);

    private static readonly VIDEO_EXTENSIONS = new Set([
        'mp4',
        'webm',
        'mov',
        'avi',
        'mkv',
        'wmv',
        'flv',
        'm4v',
    ]);

    private static readonly DOCUMENT_EXTENSIONS = new Set([
        'pdf',
        'doc',
        'docx',
        'xls',
        'xlsx',
        'ppt',
        'pptx',
        'txt',
        'rtf',
        'csv',
    ]);

    private static readonly ALL_FILE_EXTENSIONS = new Set([
        ...Array.from(FileDetectorService.IMAGE_EXTENSIONS),
        ...Array.from(FileDetectorService.AUDIO_EXTENSIONS),
        ...Array.from(FileDetectorService.VIDEO_EXTENSIONS),
        ...Array.from(FileDetectorService.DOCUMENT_EXTENSIONS),
    ]);

    static isFileUrl(text: string): boolean {
        if (!text || typeof text !== 'string') {
            return false;
        }

        try {
            const url = new URL(text);
            return this.hasFileExtension(url.pathname);
        } catch {
            return this.hasFileExtension(text);
        }
    }

    static hasFileExtension(path: string): boolean {
        if (!path) return false;

        const extension = this.extractExtension(path);
        return extension ? this.ALL_FILE_EXTENSIONS.has(extension) : false;
    }

    static extractExtension(path: string): string | null {
        if (!path) return null;

        const lastDot = path.lastIndexOf('.');
        const lastSlash = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));

        if (lastDot > lastSlash && lastDot < path.length - 1) {
            return path.substring(lastDot + 1).toLowerCase();
        }

        return null;
    }

    static getFileType(text: string): 'image' | 'audio' | 'video' | 'document' | 'file' | null {
        const extension = this.isFileUrl(text) ? this.extractExtension(text) : null;

        if (!extension) return null;

        if (this.IMAGE_EXTENSIONS.has(extension)) return 'image';
        if (this.AUDIO_EXTENSIONS.has(extension)) return 'audio';
        if (this.VIDEO_EXTENSIONS.has(extension)) return 'video';
        if (this.DOCUMENT_EXTENSIONS.has(extension)) return 'document';

        return 'file';
    }

    static extractFileName(urlOrPath: string): string {
        if (!urlOrPath) return '';

        try {
            const url = new URL(urlOrPath);
            const pathParts = url.pathname.split('/');
            return pathParts[pathParts.length - 1] || '';
        } catch {
            const pathParts = urlOrPath.split(/[/\\]/);
            return pathParts[pathParts.length - 1] || '';
        }
    }

    static isImageFile(extension: string): boolean {
        return this.IMAGE_EXTENSIONS.has(extension.toLowerCase());
    }

    static isAudioFile(extension: string): boolean {
        return this.AUDIO_EXTENSIONS.has(extension.toLowerCase());
    }

    static isVideoFile(extension: string): boolean {
        return this.VIDEO_EXTENSIONS.has(extension.toLowerCase());
    }

    static isDocumentFile(extension: string): boolean {
        return this.DOCUMENT_EXTENSIONS.has(extension.toLowerCase());
    }

    static categorizeFile(urlOrExtension: string) {
        const extension = urlOrExtension.includes('.')
            ? this.extractExtension(urlOrExtension)
            : urlOrExtension.toLowerCase();

        if (!extension) {
            return {
                isImageMessage: false,
                isAudioMessage: false,
                isVideoMessage: false,
                isFileMessage: false,
            };
        }

        const isImageMessage = this.isImageFile(extension);
        const isAudioMessage = this.isAudioFile(extension);
        const isVideoMessage = this.isVideoFile(extension);
        const isDocumentMessage = this.isDocumentFile(extension);

        return {
            isImageMessage,
            isAudioMessage,
            isVideoMessage,
            isFileMessage: isDocumentMessage || (!isImageMessage && !isAudioMessage && !isVideoMessage),
        };
    }
}

export const createMessage = (to: string, body: string) => {
    return xml('message', {type: 'chat', to}, xml('body', {}, body));
};

export const createPresence = (type?: 'unavailable') => {
    return type ? xml('presence', {type}) : xml('presence');
};

export const parseJID = (jid: string) => {
    const [local, domain, resource] = jid.split(/[@/]/);
    return {
        local,
        domain,
        resource,
        bare: `${local}@${domain}`,
        full: resource ? `${local}@${domain}/${resource}` : `${local}@${domain}`,
    };
};

export const fixDateString = (dateStr: string) => {
    if (!dateStr) return dateStr;
    if (typeof dateStr !== 'string') return dateStr;
    if (dateStr.endsWith('Z')) return dateStr;
    if (dateStr.includes('T')) return dateStr + 'Z';
    return dateStr.replace(' ', 'T') + 'Z';
};

// Utilitários para eventos de grupo
export const isGroupEvent = (stanza: any): boolean => {
    return (
        stanza.getChild('subject') && stanza.getChild('subject').children[0] === 'KEY_ROOM_GENERAL'
    );
};

export const isNewGroup = (stanza: any): boolean => {
    return (
        stanza.getChild('x') &&
        stanza.getChild('x').attrs.xmlns === 'jabber:x:conference' &&
        stanza.getChild('x').attrs.jid
    );
};

export const getEventId = (stanza: any): number | undefined => {
    const eventId: string = stanza.getChild('extraParams')?.getChild('key_room_event')?.children[0];
    return eventId ? parseInt(eventId) : undefined;
};

export const getEventBody = (stanza: any): string | undefined => {
    return stanza.getChild('extraParams')?.getChild('key_room_event_body')?.children[0];
};

export type CarbonDirection = 'sent' | 'received';

export interface UnwrappedCarbon {
    stanza: any;
    direction?: CarbonDirection;
}

/** Retorna a mensagem encaminhada pelo XEP-0280 ou a própria stanza. */
export const unwrapCarbonCopy = (stanza: any): UnwrappedCarbon => {
    if (!stanza?.is?.('message')) return {stanza};

    for (const direction of ['sent', 'received'] as const) {
        const carbon = stanza.getChild(direction);
        if (carbon?.attrs?.xmlns !== 'urn:xmpp:carbons:2') continue;

        const forwarded = carbon.getChild('forwarded');
        if (forwarded?.attrs?.xmlns !== 'urn:xmpp:forward:0') continue;

        const message = forwarded.getChild('message');
        if (message?.is?.('message')) return {stanza: message, direction};
    }

    return {stanza};
};

export const getFrom = (stanza: any): string => {
    const msgElement = unwrapCarbonCopy(stanza).stanza;
    if (msgElement.attrs.from.includes('@conference.')) {
        return msgElement.attrs.from;
    }
    return msgElement.attrs.from.split('/')[0];
};

export const getTo = (stanza: any): string => {
    const msgElement = unwrapCarbonCopy(stanza).stanza;
    return msgElement.attrs.to;
};

export const getMessageId = (stanza: any): string => {
    return unwrapCarbonCopy(stanza).stanza.attrs.id;
};

export const isCarbon = (stanza: any): boolean => {
    return Boolean(unwrapCarbonCopy(stanza).direction);
};

export const isFromGroup = (stanza: any): boolean => {
    return stanza.attrs.from.includes('@conference');
};

export const getGroupIdFromJid = (jid: string): string => {
    return jid.split('@')[0];
};

export const parseGroupEventStanza = (stanza: any): GroupEvent => {
    const from = getFrom(stanza);

    // 🔧 CORRIGIDO: Para eventos diretos, extrair groupId do extraParams/groupJid
    let groupId = getGroupIdFromJid(from);

    // Se o evento é direto (type=chat), extrair groupId do extraParams
    if (stanza.attrs.type === 'chat') {
        const groupJid = stanza.getChild('extraParams')?.getChild('groupJid')?.children[0];

        if (groupJid) {
            groupId = getGroupIdFromJid(groupJid);
            Logger.log('XMPP', '🔧 Evento direto - groupId extraído do extraParams:', {
                groupJid,
                groupId,
            });
        }
    }

    return {
        eventId: getEventId(stanza) || 1,
        eventBody: getEventBody(stanza),
        from,
        to: getTo(stanza),
        id: getMessageId(stanza),
        sentAt: new Date().toISOString(),
        time: new Date().toISOString(),
        type: stanza.attrs.type || 'groupchat',
        groupId,
    };
};

export const createJoinRoomPresence = (groupJid: string, userId: string, domain: string): any => {
  // groupJid ja contem o dominio MUC resolvido para a sala. Reconstrui-lo a
  // partir do dominio da credencial pode mandar o usuario para outra MUC apos
  // uma migracao (por exemplo, conference.labtest01 versus conference.labchat).
  const targetJid = `${groupJid.split('/')[0]}/${userId}`;

    return xml(
        'presence',
        {
            to: targetJid,
        },
        xml('x', {xmlns: 'http://jabber.org/protocol/muc'}, xml('history', {maxstanzas: '0'})),
    );
};

export const createLeaveRoomPresence = (groupJid: string, userId: string, domain: string): any => {
  // Deve sair do mesmo JID usado na entrada; veja createJoinRoomPresence.
  const targetJid = `${groupJid.split('/')[0]}/${userId}`;

    return xml('presence', {
        to: targetJid,
        type: 'unavailable',
    });
};

export const createGroupEventStanza = (to: string, eventId: number, eventBody?: string): any => {
    const id = `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    to = to.replace('labtest01', 'labchat');

    const extraParams = xml('extraParams', {xmlns: 'jabber:client'});

    if (eventId) {
        extraParams.append(xml('key_room_event', {}, String(eventId)));
    }

    if (eventBody) {
        extraParams.append(xml('key_room_event_body', {}, eventBody));
    }

    return xml(
        'message',
        {to, type: 'groupchat', id},
        xml('subject', {}, 'KEY_ROOM_GENERAL'),
        xml('body', {}),
        extraParams,
    );
};

/**
 * Cria a stanza de uma mensagem de grupo.
 *
 * Além do body e dos dados de resposta já suportados, a mensagem pode
 * transportar uma extensão própria de menções. A extensão é opcional para
 * manter compatibilidade com clientes que processam apenas o body.
 */
export const createGroupMessageStanza = (
    to: string,
    body: string,
    replyTo?: string,
    replyMsgId?: string,
    replyMsg?: string,
    mentions?: XMPPMentionMetadata,
    sender?: { id: string; name?: string },
): any => {
    const id = uuidv4();
    const messageStanza = xml('message', {
        to,
        type: 'groupchat',
        id,
    });

    messageStanza.append(xml('body', {}, body));

    messageStanza.append(
        xml('request', {
            xmlns: 'urn:xmpp:receipts',
        }),
    );

    /*
     * As menções são transportadas em uma extensão própria para não modificar
     * o contrato dos elementos padrões do XMPP. Clientes sem suporte à extensão
     * continuam conseguindo exibir normalmente o conteúdo do body.
     */
    if (mentions) {
        const usersElement = xml('users');

        for (const user of mentions.users) {
            /*
             * Cada destinatário aparece somente como um usuário da extensão.
             * O alcance global de @Todos é representado separadamente por
             * `all-mentioned`, pois ele não corresponde a um participante real.
             */
            usersElement.append(
                xml('user', {
                    id: user.id,
                    name: user.name,
                }),
            );
        }

        const mentionExtension = xml(
            'x',
            {
                xmlns: 'urn:digivox:mentions',
            },
            usersElement,
            xml('all-mentioned', {}, mentions.allMentioned ? 'true' : 'false'),
            xml('group-id', {}, mentions.groupId ?? ''),
            xml('domain', {}, mentions.domain ?? ''),
        );

        messageStanza.append(mentionExtension);
    }

    if (sender?.id) {
        messageStanza.append(xml('x', { xmlns: 'urn:digivox:meet:sender' },
            xml('user-id', {}, sender.id),
            xml('user-name', {}, sender.name ?? ''),
        ));
    }

    if (replyTo && replyMsgId && replyMsg) {
        const extraParams = xml('extraParams', {
            xmlns: 'jabber:client',
        });

        extraParams.append(xml('reply_to', {}, replyTo));
        extraParams.append(xml('reply_msg_id', {}, replyMsgId));
        extraParams.append(xml('reply_msg', {}, replyMsg));

        messageStanza.append(extraParams);
    }

    return messageStanza;
};

/** Cria uma mensagem para um usuário (chat) ou para uma sala MUC (groupchat). */
export const createChatMessageStanza = (
    to: string,
    body: string,
    chatType: 'chat' | 'groupchat',
    mentions?: XMPPMentionMetadata,
): any => {
    const stanza = createGroupMessageStanza(to, body, undefined, undefined, undefined, mentions);
    stanza.attrs.type = chatType;
    return stanza;
};
